import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit';
import orderBy from 'lodash/fp/orderBy';
import { AppState } from '.';
import {
  exceptionOf,
  SerializedException,
  TestOpsSubscriptionSource,
  TestOpsSubscriptionStatus,
  CommerceSubscription,
} from '../models';
import { CommerceSubscriptionService } from '../services';
import { Operator, Query } from '../models/query';
import { SubscriptionStatus } from '../models/subscriptionStatus';

export const COMMERCE_SUBSCRIPTION = 'commerceSubscription';

interface CommerceSubscriptionState extends EntityState<CommerceSubscription> {
  loading: boolean;
  errors: SerializedException[];
}

const commerceSubscriptionAdapter = createEntityAdapter<CommerceSubscription>();
const createInitialState = ():
CommerceSubscriptionState => commerceSubscriptionAdapter.getInitialState({
  loading: false,
  errors: [],
});

export const doGetActiveByAccountId = createAsyncThunk(
  'commerceSubscriptions/getActiveSubscriptionsByAccountId',
  async (accountId: number, { rejectWithValue }) => {
    const criterias: Query<CommerceSubscription>[] = [
      {
        field: 'accountId',
        operator: Operator.EQ,
        value: accountId,
      },
      {
        field: 'archived',
        operator: Operator.EQ,
        value: false,
      },
      {
        field: 'status',
        operator: Operator.EQ,
        value: SubscriptionStatus.ACTIVE,
      },
      { field: 'createdAt', desc: true },
    ];

    try {
      return (await CommerceSubscriptionService.getSubscriptions(...criterias));
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doResetEntity = createAction('entity/reset');

const commerceSubscriptionSlice = createSlice({
  name: COMMERCE_SUBSCRIPTION,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doGetActiveByAccountId.pending, state => {
      state.loading = true;
      state.errors = [];
    });
    builder.addCase(doGetActiveByAccountId.fulfilled, (state, action) => {
      state.loading = false;
      commerceSubscriptionAdapter.upsertMany(state, action.payload.data);
    });
    builder.addCase(doGetActiveByAccountId.rejected, state => {
      state.loading = false;
    });
  },
});

const selectCommerceSubscriptionSlice = (state: AppState) => state[COMMERCE_SUBSCRIPTION];

export const {
  selectAll: selectAllSubscriptions,
  selectIds,
  selectEntities,
} = commerceSubscriptionAdapter.getSelectors(selectCommerceSubscriptionSlice);

export const selectActiveByAccountIdAndFeature = (accountId: CommerceSubscription['accountId'], feature: CommerceSubscription['feature']) => createSelector(
  selectAllSubscriptions,
  subscriptions => orderBy('createdAt', 'desc', subscriptions).filter(
    it => it.accountId === accountId
          && it.status === TestOpsSubscriptionStatus.ACTIVE
          && it.feature === feature,
  ),
);

export const hasPaidSubscriptionByFeature = (accountId: CommerceSubscription['accountId'], feature: CommerceSubscription['feature']) => createSelector(
  selectAllSubscriptions,
  subscriptions => orderBy('createdAt', 'desc', subscriptions).filter(
    it => (it.accountId === accountId
        && it.status === TestOpsSubscriptionStatus.ACTIVE
        && it.source === TestOpsSubscriptionSource.RECURLY
        && it.feature === feature),
  )?.length > 0,
);

export const hasPaidSubscription = (accountId: CommerceSubscription['accountId']) => createSelector(
  selectAllSubscriptions,
  subscriptions => orderBy('createdAt', 'desc', subscriptions).filter(
    it => (it.accountId === accountId
        && it.status === TestOpsSubscriptionStatus.ACTIVE
        && it.source === TestOpsSubscriptionSource.RECURLY),
  )?.length > 0,
);

export default commerceSubscriptionSlice.reducer;
